/**
 * SafetyStockListView — /inventory/safety-stock  안전재고 탭(재고 › 실사 · 설정)
 *
 * 제품 하나 = 한 줄. 가용 · 보유와 견주어 모자란 것이 위(서버 정렬). 안전재고는 그 자리 입력 — 칸을 벗어나면 저장하고
 * 그 줄만 받은 값으로 바꿔 끼운다(순서는 다시 불러올 때). 회의 2026-10-15 58:40 "안전재고는 재고 쪽". 현민 2026-10-19.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { SafetyStockListQuery, SafetyStockView, SafetyStockViewList } from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { renderPagerLine } from '../components/pager-line';
import { pnTextOf } from '../components/unit-table';
import { assert } from '../lib/assert';
import { refreshKeepingFocus } from '../lib/keep-focus';
import { escapeHtml } from '../lib/cells';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';

interface SafetyStockData {
  query: SafetyStockListQuery;           // 찾기 · 모자란 것만 · 페이지 시작 — 서버가 거른다
  list: SafetyStockViewList | null;      // 받은 묶음. null = 아직
  loading: boolean;
  error: string;
  rowError: { id: string; message: string } | null;   // 저장이 막힌 줄 하나 — 그 줄 밑에 글
}

const TAB_HREF = '/inventory/safety-stock';
const QUERY_DEBOUNCE_MS = 300;
const INVALID_WORD = '0 이상의 정수이거나 비워야 합니다';

export class SafetyStockListView extends SolaView<SafetyStockData> {
  override events = {
    click:  { '@pn': 'handleOpenPn', '@page': 'handlePage' },
    change: { '@only-short': 'handleOnlyShort', '@safety-input': 'handleSafetyInput' },
    input:  { '@query': 'handleQuery' },
  };

  private queryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
    this.data = { query: { q: '', only_short: false, offset: 0 }, list: null, loading: true, error: '', rowError: null };
  }

  override getTitle(): string {
    return '필수 재고 — HCLTech';
  }

  override onMount(): void {
    void this.load();
  }

  /** 본문(.pv-body)을 다시 그린다 — 찾기 칸에 치던 포커스 · 커서는 지킨다(lib/keep-focus, 2026-10-28) */
  private refreshBody(): void {
    refreshKeepingFocus(this._rootElement ?? null, '.pv-body', () => this.refreshRegion('.pv-body', () => this.renderBody()));
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF);
    assert(place !== null, `안전재고 탭이 메뉴에 없다: ${TAB_HREF} — MENU_GROUPS 재고 › 실사 · 설정의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-safety-stock pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  // ─── 불러오기 ───
  /** 지금 query 로 한 묶음. 실패면 error 글. 끝나면 본문 다시 그림 */
  private async load(): Promise<void> {
    this.data = { ...this.data, loading: true, error: '', rowError: null };
    this.refreshBody();
    try {
      const list = await api.fetchSafetyStocks(this.data.query);
      this.data = { ...this.data, list, loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody();
  }

  // ─── 그리기 ───
  private renderBody(): string {
    const { list, loading, error } = this.data;
    const rows = list === null ? [] : list.rows;
    const emptyWord = loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '맞는 줄이 없습니다';
    const body = rows.length === 0 ? `<tr><td colspan="7" class="text-muted">${emptyWord}</td></tr>` : rows.map((row, index) => this.renderRow(row, this.data.query.offset + index)).join('');

    return `${this.renderToolbar()}
      <table class="pv-table pv-safety">
        <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>품목</th><th>가용</th><th>보유</th><th>필수 재고</th><th>모자람</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  /** 도구 줄 — 찾기 · 모자란 것만 · 페이징 */
  private renderToolbar(): string {
    const { query, list } = this.data;
    return `
      <div class="pc-filter-bar">
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="query" placeholder="파트넘버 · 품목" value="${escapeHtml(query.q)}">
        </label>
        <label class="pc-check"><input type="checkbox" data-action="only-short" ${query.only_short ? 'checked' : ''}> 모자란 것만</label>
        <span class="pv-spacer"></span>
        ${list === null ? '' : renderPagerLine(list, 'page')}
      </div>`;
  }

  /** 제품 한 줄 — 파트넘버는 제품 상세 새 탭, 안전재고는 그 자리 입력(비우면 안 봄) */
  private renderRow(row: SafetyStockView, no: number): string {
    const rowError = this.data.rowError;
    const errorLine = rowError !== null && rowError.id === row.hclt_product_id ? `<span class="ss-row-error">${escapeHtml(rowError.message)}</span>` : '';
    const safetyText = row.safety_stock === null ? '' : String(row.safety_stock);

    return `
      <tr>
        ${rowNoCell(no, 0)}
        <td class="pl-main"><button type="button" class="pv-pn mono" data-action="pn" data-id="${row.hclt_product_id}"><b>${escapeHtml(pnTextOf(row))}</b></button></td>
        <td class="pl-main pl-desc"><div class="pl-clamp">${row.description === null ? '' : escapeHtml(row.description)}</div></td>
        <td class="pl-main pl-qty">${row.available_count}</td>
        <td class="pl-sub pl-qty">${row.held_count}</td>
        <td class="pl-main"><input type="number" min="0" step="1" class="ss-input mono" data-action="safety-input" data-id="${row.hclt_product_id}" value="${safetyText}" placeholder="안 봄">${errorLine}</td>
        <td class="pl-main pl-qty ${row.shortage > 0 ? 'ss-short' : 'text-muted'}">${row.shortage}</td>
      </tr>`;
  }

  // ─── 핸들러 ───
  /** 파트넘버 → 제품 상세 새 탭 */
  handleOpenPn(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '파트넘버 버튼은 data-id(hclt_product_id)를 가진다 — renderRow 가 붙였다');
    window.open(`/products/${encodeURIComponent(id)}`, '_blank', 'noopener');
  }

  /** 안전재고 칸을 벗어남 — 바뀌었으면 저장하고 그 줄만 바꿔 끼운다. 음수 · 소수는 보내지 않고 그 줄에 글 */
  async handleSafetyInput(_e: Event, el: HTMLElement): Promise<void> {
    const id = el.dataset.id ?? '';
    assert(id !== '', '안전재고 칸은 data-id(hclt_product_id)를 가진다 — renderRow 가 붙였다');
    const text = (el as HTMLInputElement).value.trim();
    const valueOrNull = text === '' ? null : Number(text);
    const bValid = valueOrNull === null || (Number.isInteger(valueOrNull) && valueOrNull >= 0);
    if (!bValid) {
      this.data = { ...this.data, rowError: { id, message: INVALID_WORD } };
      this.refreshBody();
      return;
    }
    try {
      const saved = await api.updateSafetyStock(id, valueOrNull);
      this.data = { ...this.data, list: replaceRow(this.data.list, saved), rowError: null };
    } catch (e) {
      this.data = { ...this.data, rowError: { id, message: e instanceof Error ? e.message : String(e) } };
    }
    this.refreshBody();
  }

  handleOnlyShort(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, only_short: (el as HTMLInputElement).checked, offset: 0 } };
    void this.load();
  }

  handleQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, q: (el as HTMLInputElement).value, offset: 0 } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.load(); }, QUERY_DEBOUNCE_MS);
  }

  /** 쪽 이동 — 버튼의 data-offset 으로 */
  handlePage(_e: Event, el: HTMLElement): void {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, query: { ...this.data.query, offset } };
    void this.load();
  }
}

/** 받은 한 줄로 묶음의 같은 제품 줄을 바꾼다 — 순서는 그대로(다시 불러올 때 서버가 맞춘다) */
function replaceRow(list: SafetyStockViewList | null, saved: SafetyStockView): SafetyStockViewList | null {
  if (list === null) return null;
  return { ...list, rows: list.rows.map((row) => row.hclt_product_id === saved.hclt_product_id ? saved : row) };
}
